import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Briefcase,
  Users,
  Search,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Building2,
  FileText,
  BarChart3,
  Target,
  Zap,
  Shield,
  Clock,
  PoundSterling
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getSalesPhones, joinPhones } from '../lib/salesPhones';
import { useLocalPrice } from '../lib/localCurrency';

export default function RecruiterPage() {
  const { branding, user } = useAuth();
  const localPrice = useLocalPrice(user?.country);
  const money = (gbp) => localPrice.format(gbp, { decimals: 0 });
  const salesPhones = getSalesPhones(branding);
  const contactPhone = salesPhones.length ? joinPhones(salesPhones, 'or') : '';
  const getFeaturePath = (feature) => {
    switch (feature.title) {
      case 'Pay-Per-Application Jobs':
        return '/employer/post-job';
      case 'Job Advert Credit':
        return '/services?service=job_advert_credit';
      case 'CV Search':
        return '/services?service=cv_search';
      case 'Direct Recruitment':
        return '/services?service=direct_recruitment';
      default:
        return null;
    }
  };

  const features = [
    {
      icon: Briefcase,
      title: 'Pay-Per-Application Jobs',
      description: 'Set a budget cap and only pay for the applications you receive. Free to post.',
      price: 'Free to post',
      cta: 'Post a job'
    },
    {
      icon: FileText,
      title: 'Job Advert Credit',
      description: 'Pay upfront and advertise your role to over 26 million candidates.',
      price: `From ${money(89)}+VAT`,
      cta: 'Buy a credit'
    },
    {
      icon: Search,
      title: 'CV Search',
      description: 'Find talent faster with access to our candidate database.',
      price: `From ${money(99)}+VAT`,
      cta: 'Search talent'
    },
    {
      icon: Users,
      title: 'Direct Recruitment',
      description: 'Expert recruitment team finds and screens candidates for you.',
      price: `From ${money(299)}+VAT`,
      cta: 'Get started'
    }
  ];

  const stats = [
    { value: '70,000', label: 'Unique CVs uploaded per week' },
    { value: '80,000', label: 'Applications made every day' },
    { value: '1 billion', label: 'Brand advertising views' },
    { value: '18 seconds', label: 'New candidate joins' }
  ];

  const benefits = [
    'Access to millions of active job seekers',
    'AI-powered candidate matching',
    'Real-time application notifications',
    'Comprehensive analytics dashboard',
    'Dedicated account support',
    'ATS integration available'
  ];

  const brands = ['Aldi', 'Metropolitan Police', 'O2', 'Ocado', 'Holland & Barrett', 'Costa Coffee'];

  return (
    <div className="min-h-screen" data-testid="recruiter-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#003366] to-[#004080] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Advertise jobs and find your perfect candidate
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Connecting employers with people quickly through the UK's most effective 
              job advert and CV Search service.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/employer/post-job">
                <Button className="bg-[#E11D48] hover:bg-[#E11D48]/90 rounded-full px-8 h-12" data-testid="post-job-cta">
                  Post a Job
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 h-12">
                {contactPhone ? `Contact Sales: ${salesPhones.join(' / ')}` : 'Contact Sales'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <Card key={idx} className="border-2 hover:border-[#003366] transition-colors group">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-[#003366]/10 flex items-center justify-center mb-4 group-hover:bg-[#003366] transition-colors">
                    <feature.icon className="w-6 h-6 text-[#003366] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{feature.description}</p>
                  <div className="mb-4">
                    <span className="font-bold text-[#003366] text-lg">{feature.price}</span>
                  </div>
                  {getFeaturePath(feature) ? (
                    <Link to={getFeaturePath(feature)}>
                      <Button className="w-full bg-[#003366] hover:bg-[#003366]/90 rounded-lg">
                        {feature.cta}
                      </Button>
                    </Link>
                  ) : (
                    <Button className="w-full bg-[#003366] hover:bg-[#003366]/90 rounded-lg">
                      {feature.cta}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="py-12 bg-[#F0F4F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-lg text-muted-foreground mb-8">
            Recruitment solutions trusted by the UK's biggest brands
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-8">
            {brands.map((brand, idx) => (
              <div key={idx} className="px-6 py-3 bg-white rounded-lg shadow-sm">
                <span className="font-semibold text-gray-500">{brand}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700">Platform Stats</Badge>
            <h2 className="text-3xl font-bold">A new candidate joins WorksRun every 18 seconds</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-[#003366]">{stat.value}</div>
                <div className="text-muted-foreground mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-[#F0F4F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 gradient-ai text-white">Why Choose WorksRun</Badge>
              <h2 className="text-3xl font-bold mb-6">The smart way to hire</h2>
              <p className="text-muted-foreground mb-8">
                Our AI-powered platform helps you find the right candidates faster, 
                with tools designed to streamline your recruitment process.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Target, title: 'AI Matching', desc: 'Smart candidate recommendations' },
                { icon: Zap, title: 'Fast Hiring', desc: 'Average time to hire: 14 days' },
                { icon: Shield, title: 'Quality Assured', desc: 'Verified candidate profiles' },
                { icon: BarChart3, title: 'Analytics', desc: 'Track your hiring metrics' },
              ].map((item, idx) => (
                <Card key={idx} className="bg-white border-0 shadow-sm">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center mb-3">
                      <item.icon className="w-5 h-5 text-[#003366]" />
                    </div>
                    <h3 className="font-semibold mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Simple, transparent pricing</h2>
            <p className="text-muted-foreground">Choose the solution that works for your hiring needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="border-2">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold mb-2">Pay-Per-Application</h3>
                <p className="text-muted-foreground text-sm mb-6">Only pay for applications received</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#003366]">Free</span>
                  <span className="text-muted-foreground"> to post</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {['Set your budget cap', 'Pay per application', 'Cancel anytime', 'Basic analytics'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/employer/post-job">
                  <Button className="w-full bg-[#003366] hover:bg-[#003366]/90">Get Started</Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#E11D48] relative">
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E11D48]">Most Popular</Badge>
              <CardContent className="p-8">
                <h3 className="text-xl font-bold mb-2">Job Advert Credit</h3>
                <p className="text-muted-foreground text-sm mb-6">Premium visibility for your roles</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#003366]">{money(89)}</span>
                  <span className="text-muted-foreground">+VAT / job</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {['Featured listing', 'Priority placement', '30 day posting', 'Full analytics', 'Unlimited applications'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/pricing">
                  <Button className="w-full bg-[#E11D48] hover:bg-[#E11D48]/90">Buy Credits</Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold mb-2">CV Search</h3>
                <p className="text-muted-foreground text-sm mb-6">Access our candidate database</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#003366]">{money(99)}</span>
                  <span className="text-muted-foreground">+VAT / month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {['Search CVs directly', 'Contact candidates', 'Save candidate lists', 'Advanced filters', 'Monthly subscription'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/recruiter-dashboard?tab=cv_search">
                  <Button className="w-full bg-[#003366] hover:bg-[#003366]/90">Start Searching</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#003366] text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to advertise your job?</h2>
          <p className="text-white/80 mb-8">
            {contactPhone ? `Call us now on ${contactPhone} to discuss` : 'Get in touch to discuss'} your hiring needs and find the right online job ad solution for you!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/employer/post-job">
              <Button className="bg-[#E11D48] hover:bg-[#E11D48]/90 rounded-full px-8 h-12">
                Post a Job Now
              </Button>
            </Link>
            <Link to="/register?type=employer">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 h-12">
                Create Employer Account
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
